/**
 * Skewness and kurtosis in the three families software uses, after Joanes & Gill (1998),
 * "Comparing measures of sample skewness and kurtosis", The Statistician 47, 183–189 —
 * the same three types as R's `e1071::skewness(x, type = 1:3)` and `kurtosis(x, type = 1:3)`.
 *
 * With m_k = Σ(x − x̄)^k ÷ n the central moments and n the sample size:
 *
 * | Type | Skewness                              | Excess kurtosis                                  | Used by                     |
 * | ---- | ------------------------------------- | ------------------------------------------------ | --------------------------- |
 * | 1    | g1 = m3 ÷ m2^1.5                      | g2 = m4 ÷ m2² − 3                                | textbooks, population value |
 * | 2    | G1 = g1 × √(n(n − 1)) ÷ (n − 2)       | G2 = ((n − 1) ÷ ((n − 2)(n − 3))) × ((n + 1)g2 + 6) | SPSS, SAS, Excel SKEW/KURT |
 * | 3    | b1 = m3 ÷ s³ = g1 × ((n − 1)/n)^1.5   | b2 = m4 ÷ s⁴ − 3 = (g2 + 3)(1 − 1/n)² − 3       | Minitab                     |
 *
 * Standard errors are the exact null-hypothesis (normal population) values SPSS prints:
 * SE(G1) = √(6n(n − 1) ÷ ((n − 2)(n + 1)(n + 3))) and
 * SE(G2) = 2 × SE(G1) × √((n² − 1) ÷ ((n − 3)(n + 5))).
 */

import { normalCdf } from "@/lib/stats/distributions";

export interface SkewnessTypes {
  /** Type 1, g1. */
  g1: number;
  /** Type 2, G1 (SPSS, SAS, Excel SKEW). Needs n ≥ 3. */
  adjusted: number | null;
  /** Type 3, b1 (Minitab). */
  b1: number;
}

export interface KurtosisTypes {
  /** Type 1, g2 (excess). */
  g2: number;
  /** Type 2, G2 (SPSS, SAS, Excel KURT). Needs n ≥ 4. */
  adjusted: number | null;
  /** Type 3, b2 (Minitab, excess). */
  b2: number;
}

export interface MomentTest {
  value: number;
  se: number;
  z: number;
  /** Two-sided p-value from the standard normal. */
  p: number;
  lower: number;
  upper: number;
}

export interface MomentsReport {
  n: number;
  mean: number;
  median: number;
  /** Sample standard deviation, n − 1. */
  sd: number;
  /** Central moments m_k = Σ(x − x̄)^k ÷ n. */
  m2: number;
  m3: number;
  m4: number;
  /** Pearson's second skewness coefficient, 3(mean − median) ÷ s. */
  pearsonSecond: number;
  skewness: SkewnessTypes;
  kurtosis: KurtosisTypes;
  skewTest: MomentTest | null;
  kurtosisTest: MomentTest | null;
}

export const Z_95 = 1.959_963_984_540_054;

export function skewnessStandardError(n: number): number | null {
  if (n < 3) {
    return null;
  }
  return Math.sqrt((6 * n * (n - 1)) / ((n - 2) * (n + 1) * (n + 3)));
}

export function kurtosisStandardError(n: number): number | null {
  const se = skewnessStandardError(n);
  if (se === null || n < 4) {
    return null;
  }
  return 2 * se * Math.sqrt((n * n - 1) / ((n - 3) * (n + 5)));
}

function centralMoments(values: readonly number[]) {
  const n = values.length;
  let sum = 0;
  for (const value of values) {
    sum += value;
  }
  const mean = sum / n;
  let m2 = 0;
  let m3 = 0;
  let m4 = 0;
  for (const value of values) {
    const d = value - mean;
    m2 += d * d;
    m3 += d * d * d;
    m4 += d * d * d * d;
  }
  return { mean, m2: m2 / n, m3: m3 / n, m4: m4 / n };
}

function medianOf(values: readonly number[]): number {
  const data = [...values].sort((a, b) => a - b);
  const mid = Math.floor(data.length / 2);
  return data.length % 2 === 0 ? (data[mid - 1] + data[mid]) / 2 : data[mid];
}

function makeTest(value: number, se: number): MomentTest {
  const z = value / se;
  return {
    value,
    se,
    z,
    p: 2 * (1 - normalCdf(Math.abs(z))),
    lower: value - Z_95 * se,
    upper: value + Z_95 * se,
  };
}

/** Null when there are fewer than three values or no spread. */
export function momentsReport(values: readonly number[]): MomentsReport | null {
  const n = values.length;
  if (n < 3) {
    return null;
  }
  const { mean, m2, m3, m4 } = centralMoments(values);
  if (m2 === 0) {
    return null;
  }
  const g1 = m3 / m2 ** 1.5;
  const g2 = m4 / (m2 * m2) - 3;
  const sd = Math.sqrt((m2 * n) / (n - 1));
  const adjustedSkew = (g1 * Math.sqrt(n * (n - 1))) / (n - 2);
  const adjustedKurt =
    n >= 4 ? ((n - 1) / ((n - 2) * (n - 3))) * ((n + 1) * g2 + 6) : null;
  const median = medianOf(values);
  const seSkew = skewnessStandardError(n);
  const seKurt = kurtosisStandardError(n);
  return {
    n,
    mean,
    median,
    sd,
    m2,
    m3,
    m4,
    pearsonSecond: (3 * (mean - median)) / sd,
    skewness: {
      g1,
      adjusted: adjustedSkew,
      b1: g1 * ((n - 1) / n) ** 1.5,
    },
    kurtosis: {
      g2,
      adjusted: adjustedKurt,
      b2: (g2 + 3) * (1 - 1 / n) ** 2 - 3,
    },
    skewTest: seSkew === null ? null : makeTest(adjustedSkew, seSkew),
    kurtosisTest:
      seKurt === null || adjustedKurt === null
        ? null
        : makeTest(adjustedKurt, seKurt),
  };
}

export type SkewLabel = "symmetric" | "moderate" | "high";

/** Bulmer's (1979) rule of thumb: |skew| < 0.5 symmetric, 0.5–1 moderate, > 1 high. */
export function skewLabel(skew: number): SkewLabel {
  const size = Math.abs(skew);
  if (size < 0.5) {
    return "symmetric";
  }
  return size <= 1 ? "moderate" : "high";
}

export type TailLabel = "light" | "normal" | "heavy";

/** Excess kurtosis within ±0.5 of zero is read as normal-like tails; a rough guide only. */
export function tailLabel(excess: number): TailLabel {
  if (excess > 0.5) {
    return "heavy";
  }
  return excess < -0.5 ? "light" : "normal";
}

export interface HistogramBin {
  from: number;
  to: number;
  count: number;
}

/** Equal-width bins; k defaults to Sturges' rule, ⌈1 + log₂ n⌉, at least 3. */
export function histogramBins(
  values: readonly number[],
  k?: number
): HistogramBin[] {
  const n = values.length;
  const lo = Math.min(...values);
  const hi = Math.max(...values);
  if (n === 0 || hi === lo) {
    return [];
  }
  const bins = Math.max(3, k ?? Math.ceil(1 + Math.log2(n)));
  const width = (hi - lo) / bins;
  const out: HistogramBin[] = Array.from({ length: bins }, (_, i) => ({
    from: lo + i * width,
    to: lo + (i + 1) * width,
    count: 0,
  }));
  for (const value of values) {
    const index = Math.min(bins - 1, Math.floor((value - lo) / width));
    out[index].count += 1;
  }
  return out;
}
